import React, { useState } from "react";
import { CircularProgress, Grid, IconButton } from "@mui/material";
import { useIntl } from "react-intl";
import { Modal } from "@packsize/components";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";

import { DiffView } from "@/components/diff-view";
import { useProductionGroupHistory } from "@/hooks/production-groups";
import type { Corrugate } from "@/schemas/material/corrugate";
import type { MachineGroup } from "@/schemas/machine-group";
import type { ProductionGroup } from "@/schemas/production-group";
import { sanitizeProductionGroupAuditData } from "@/utils/sanitize-audit-data";

interface ProductionGroupHistoryButtonProps {
	readonly productionGroup: ProductionGroup;
	readonly corrugates: Corrugate[];
	readonly machineGroups: MachineGroup[];
}

export const ProductionGroupHistoryButton: React.FC<ProductionGroupHistoryButtonProps> = ({
	productionGroup,
	corrugates,
	machineGroups,
}) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { historyData, isLoading } = useProductionGroupHistory(productionGroup.id, { enabled: isOpen });

	return (
		<>
			<Modal
				onClose={() => setIsOpen(false)}
				title={intl.formatMessage(
					{
						id: "Production Group History: {alias}",
						defaultMessage: "Production Group History: {alias}",
					},
					{ alias: productionGroup.alias },
				)}
				open={isOpen}
				size="xl"
			>
				{isLoading === true && (
					<Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
						<CircularProgress color="info" data-testid="production-group-history-button__progress__loading" />
					</Grid>
				)}
				{isLoading === false && historyData != null && (
					<DiffView
						data={historyData}
						compareAll
						actionColumn={null}
						additionalDataTransform={(data) => sanitizeProductionGroupAuditData(data, corrugates, machineGroups)}
					/>
				)}
			</Modal>
			<IconButton
				onClick={() => setIsOpen(true)}
				data-testid="production-group-history-button__button__open-modal"
				data-pendoid="production-groups__button__history"
			>
				<HistoryOutlined sx={{ fontSize: 24, color: "text.primary" }} />
			</IconButton>
		</>
	);
};
